import { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import CustomMultiSelect from "../../../forms/selects/CustomMultiSelect";
import { SelectData } from "../../../forms/selects/selectData";
import { CourseTopicData } from "../../../../models/courseData";
import { CourseLanguageData } from "../../../../models/courseData";
import { useFormikContext } from "formik";
import { ParticipationType } from "../../../../models/targetGroupData";
import { useEffect } from "react";
import { mapToTargetGroupTranslationKey } from "../../../../models/enums/eventType.enum";
import {
  getCurrentLanguage,
  getDynamicTranslation,
} from "../../../core/localization/localizationUtils";


interface EventFilterProps {
  isThematicFilter?: boolean;
  isTargetGroupFilter?: boolean;
  allOptions: CourseTopicData[] | CourseLanguageData[] | ParticipationType[];
  selectedOptions: string[];
  setThematicFiltersEnabled?: Dispatch<SetStateAction<boolean>>;
  allMembers?: ParticipationType;
}

interface FilterFormValues {
  language_filters: CourseLanguageData[];
  thematic_filters: CourseTopicData[];
  target_group_filters: ParticipationType[];
}

/**
 * Translates a course language into the current UI locale.
 *
 * @param {CourseLanguageData} language - The language object provided by the backend/store.
 * @returns {string} The localized display name of the language, or the fallback name if translation fails.
 */
const translateLanguageLabel = (language: CourseLanguageData): string => {
  try {
    const locale = getCurrentLanguage();
    const displayNames = new Intl.DisplayNames([locale], { type: "language" });

    return displayNames.of(language.language) || language.name;
  } catch (error) {
    return language.name;
  }
};

/**
 * Maps the target group label to a translation key and returns its localized value.
 *
 * @param {ParticipationType} targetGroup - The target group object from the backend/store.
 * @returns {string} The translated label for the target group.
 */
const translateTargetGroupLabel = (targetGroup: ParticipationType): string => {
  const translationKey = mapToTargetGroupTranslationKey(targetGroup.label);

  return getDynamicTranslation(translationKey, "common");
};

/**
 * Component that holds filters for events.
 * Currently, two filters are being applied: a language based, and a topic/thematic based
 * filter.
 * Language filter is default.
 */
const EventFilterField = (props: EventFilterProps) => {
  const {
    isTargetGroupFilter,
    isThematicFilter,
    allOptions,
    selectedOptions,
    setThematicFiltersEnabled,
    allMembers,
  } = props;
  const { t } = useTranslation(["events"]);

  const { setFieldValue } = useFormikContext<FilterFormValues>();

  // default filter is language filter
  let id = "language-filter";
  let label = t("eventFilter.filters.languageFilter", { ns: "events" });
  let labelId = "language-filter";
  let fieldValueName = "language_filters";

  if (isThematicFilter === true) {
    id = "thematic-filter";
    label = t("eventFilter.filters.thematicFilter", { ns: "events" });
    labelId = "thematic-filter";
    fieldValueName = "thematic_filters";
  }

  const vals = selectedOptions.map((option) => String(option));
  if (isTargetGroupFilter === true) {
    id = "target-group-filter";
    label = t("eventFilter.filters.targetGroupFilter", { ns: "events" });
    labelId = "target-group-filter";
    fieldValueName = "target_group_filters";
  }

  useEffect(() => {
    if (
      allMembers &&
      selectedOptions.length === 1 &&
      selectedOptions[0].toString() === allMembers.id
    ) {
      setFieldValue(fieldValueName, []);
    }
  }, []);

  /**
   * Changes the desired field.
   * @param {string} value - The input value.
   */
  const handleChange = (value: string) => {
    let newArray = [...selectedOptions, value];

    if (value) {
      if (allMembers && value !== allMembers.id) {
        newArray = [value];
      }
      setFieldValue(fieldValueName, newArray);
      setThematicFiltersEnabled?.(true);
    } else {
      newArray = [];
      setThematicFiltersEnabled?.(false);
    }
    setFieldValue(fieldValueName, newArray);
  };

  /**
   * Prepares the selectable options with their display labels and sorts them alphabetically
   * according to the active UI language.
   */
  const sortedOptions = allOptions
    .map((x) => {
      const optionLabel =
        "language" in x
          ? x.name
          : "label" in x
            ? translateTargetGroupLabel(x)
            : "name" in x
              ? x.name
              : "";

      return {
        key: x.id,
        value: String(x.id),
        label: optionLabel,
      };
    })
    .sort((firstOption, secondOption) =>
      (firstOption.label || "").localeCompare(
        secondOption.label || "",
        getCurrentLanguage()
      )
    ) as SelectData[];


  return (
    <CustomMultiSelect
      defaultOptions={vals}
      name={id}
      id={id}
      label={label}
      labelId={labelId}
      hasCustomChangeHandler
      customChangeHandler={(value) => {
        handleChange(value.toString());
      }}
      options={sortedOptions}
      activateUseEffect={false}
    />
  );
};

export default EventFilterField;
